import type { AgentEvent, ChatMessage, MemoryConfirmation, TodoItem, ToolCall } from "../types";

/// 事件应用结果
export interface EventApplyResult {
  messages: ChatMessage[];
  todos?: TodoItem[];
  memoryConfirmations?: MemoryConfirmation[];
  exited?: boolean;
  costInfo?: string;
  errorMessage?: string;
}

/// 将一个 AgentEvent 应用到消息数组上，返回新数组（不可变更新）
export function applyEventToMessages(
  prev: ChatMessage[],
  event: AgentEvent,
): ChatMessage[] {
  if ("TextDelta" in event) {
    const delta = event.TextDelta.content;
    const last = prev[prev.length - 1];
    if (last?.role === "assistant") {
      return [
        ...prev.slice(0, -1),
        { ...last, content: last.content + delta, streaming: true },
      ];
    }
    return [
      ...prev,
      { id: crypto.randomUUID(), role: "assistant", content: delta, streaming: true, timestamp: Date.now() },
    ];
  }

  if ("ThinkingDelta" in event) {
    const delta = event.ThinkingDelta.content;
    const last = prev[prev.length - 1];
    if (last?.role === "assistant") {
      return [
        ...prev.slice(0, -1),
        { ...last, thinking: (last.thinking ?? "") + delta, streaming: true },
      ];
    }
    return [
      ...prev,
      { id: crypto.randomUUID(), role: "assistant", content: "", thinking: delta, streaming: true, timestamp: Date.now() },
    ];
  }

  if ("MessageComplete" in event) {
    const complete = event.MessageComplete.content;
    const last = prev[prev.length - 1];
    if (last?.role === "assistant" && last.streaming) {
      return [...prev.slice(0, -1), { ...last, streaming: false }];
    }
    if (complete) {
      return [
        ...prev,
        { id: crypto.randomUUID(), role: "assistant", content: complete, streaming: false, timestamp: Date.now() },
      ];
    }
    return prev;
  }

  if ("ToolUse" in event) {
    const tool = event.ToolUse;
    const toolCall: ToolCall = {
      toolUseId: tool.tool_use_id, toolName: tool.tool_name,
      input: tool.input, status: "pending", needsApproval: false,
    };
    const last = prev[prev.length - 1];
    if (last?.role === "assistant") {
      return [...prev.slice(0, -1), { ...last, toolCalls: [...(last.toolCalls ?? []), toolCall] }];
    }
    return prev;
  }

  if ("ToolResult" in event) {
    const result = event.ToolResult;
    const last = prev[prev.length - 1];
    if (last?.role === "assistant" && last.toolCalls) {
      const updated = last.toolCalls.map((tc) =>
        tc.toolUseId === result.tool_use_id ? { ...tc, output: result.output, status: "completed" as const } : tc,
      );
      return [...prev.slice(0, -1), { ...last, toolCalls: updated }];
    }
    return prev;
  }

  if ("ToolApprovalRequest" in event) {
    const req = event.ToolApprovalRequest;
    const last = prev[prev.length - 1];
    if (last?.role === "assistant" && last.toolCalls) {
      const exists = last.toolCalls.some((tc) => tc.toolUseId === req.tool_use_id);
      if (exists) {
        const updated = last.toolCalls.map((tc) =>
          tc.toolUseId === req.tool_use_id ? { ...tc, needsApproval: true } : tc,
        );
        return [...prev.slice(0, -1), { ...last, toolCalls: updated }];
      }
    }
    const toolCall: ToolCall = {
      toolUseId: req.tool_use_id, toolName: req.tool_name,
      input: req.input, status: "pending", needsApproval: true,
    };
    if (last?.role === "assistant") {
      return [...prev.slice(0, -1), { ...last, toolCalls: [...(last.toolCalls ?? []), toolCall] }];
    }
    return [
      ...prev,
      { id: crypto.randomUUID(), role: "assistant", content: "", toolCalls: [toolCall], timestamp: Date.now() },
    ];
  }

  if ("Error" in event) {
    return [
      ...prev,
      { id: crypto.randomUUID(), role: "assistant", content: `**Error:** ${event.Error.message}`, timestamp: Date.now() },
    ];
  }

  if ("Exited" in event) {
    return [
      ...prev,
      { id: crypto.randomUUID(), role: "system", content: event.Exited.reason ?? "", systemKind: "exited", timestamp: Date.now() },
    ];
  }

  return prev;
}
